import angular from 'angular';
import optionsTemplate from './options.template.html';

/*****************************************************************************
 * Component: itemOptionsContent
 *
 * This is the component to be used to manage the list of <b>ItemOption</b>
 * objects for a product item in the addChangeItemDialog. This will utilize
 * the addChangeItemDialog's component controller for shared data management.
 *****************************************************************************/
export const itemOptionsContentComponent = {
  require: { dialogCtrl: '^^addChangeItemDialogContent' },
  template: optionsTemplate,
  controller: ItemOptionsController,
  controllerAs: '$ioCtrl'
};

/*******************************************************************************
 * ItemOptionsController */
/**
 * Controller for the options tab of the add/change item dialog.
 *******************************************************************************/
/* @ngInject */ function ItemOptionsController(
  $scope,
  $timeout,
  AddChangeItemOptionDialog,
  Alert,
  BooksSvc,
  BusyScreen,
  CommonUIGridTemplates,
  ItemAdminService,
  SERVER,
  uiGridConstants
) {
  /*****************************************************************************
   * Properties
   *****************************************************************************/
  /** Controller Instance. */ var mThis = this;
  /** Current temporary item option ID to use. */ var mCurrTempID = 0;
  /** AddChangeItemOptionDialog shown. */ var mOptionDialogActive = false;
  /** Source Item Reference. */ mThis.sourceOptionItem = null;

  /** Quantity Change Column Definition. */
  var mColDefQtyChange = { name: 'qtyChange', maxWidth: 110 };

  /*-------------------------------------------------*/
  /* Edit Column cell template for the options grid. */
  /*-------------------------------------------------*/
  var mEditOptionCellTemplate =
    '<div class="ui-grid-cell-contents">' +
    '<span class="csm-grid-space-target glyphicon glyphicon-edit" style="cursor:pointer" ng-click="grid.appScope.$ioCtrl.handleOption(row.entity)">' +
    '</span>' +
    '</div>';

  /*----------------------------------------*/
  /* Grid to load and display item options. */
  /* This is used in the options tab.       */
  /*----------------------------------------*/
  mThis.dgOptions = {
    enableColumnMenus: false,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableSorting: false,
    enableRowSelection: true,
    enableFullRowSelection: false,
    enableRowHeaderSelection: true,
    multiSelect: false,
    enableSelectAll: false,
    minRowsToShow: 3,
    maxRowsToShow: 5,
    onRegisterApi: function (gridApi) {
      mThis.dgOptions.api = gridApi;
    },
    data: [],
    columnDefs: [
      {
        name: 'active?',
        cellTemplate:
          '<input type="checkbox" ng-checked="row.entity.active" ng-click="grid.appScope.$ioCtrl.handleActiveCheckbox($event,row, grid)">',
        maxWidth: 80,
        type: 'boolean'
      },
      {
        name: 'option',
        labelFunction: optionNameLabelFunction,
        maxWidth: 200
      },
      {
        name: 'value'
      },
      {
        name: 'priceChange',
        cellFilter: 'currency',
        maxWidth: 120
      },
      mColDefQtyChange,
      {
        field: 'edit',
        displayName: ' ',
        maxWidth: 10,
        enableSorting: false,
        cellTemplate: mEditOptionCellTemplate
      },
      {
        field: 'delete',
        displayName: ' ',
        maxWidth: 10,
        enableSorting: false,
        cellTemplate: CommonUIGridTemplates.CELL_DELETE_ROW,
        appScope: $scope
      }
    ]
  };

  /***************************************************************************
   * handleActiveCheckbox */
  /**
   * Checkbox click handler. The checkbox is not using a an ng-model or an
   * ng-model with a getter/setter because we need to check the list of
   * Inventory Products to see if the item option is currently being used
   * before updating the active property.
   *
   * The checkbox is updated to reflect the active property only after checking
   * to ensure the user can uncheck it.
   ***************************************************************************/
  mThis.handleActiveCheckbox = function (event, row, grid) {
    /*-------------------------------------------------------*/
    /* Row was not active before being clicked, make active. */
    /*-------------------------------------------------------*/
    if (!row.entity.active) row.entity.active = true;
    /*-----------------*/
    /* Row was active. */
    /*-----------------*/ else {
      /*-----------------------------------------------*/
      /* Check the item to make sure it is a product   */
      /* If not, the checkOptionID will not exist.     */
      /* Row was active before click, check for for    */
      /* inventory products associated with the option */
      /* to make sure counts have been zeroed out.     */
      /*-----------------------------------------------*/
      if (
        mThis.dialogCtrl.uiFlags.isProduct &&
        mThis.dialogCtrl.checkOptionID(row.entity.id)
      ) {
        Alert.info(
          'This option has been tracked in inventory, and cannot be removed. Zero out Count and Reserved for products using this option.',
          'Cannot Remove Option'
        );
      } else row.entity.active = false;
    }

    /*-------------------------------------------*/
    /* Set checkbox to reflect new active state. */
    /*-------------------------------------------*/
    event.currentTarget.checked = row.entity.active;
  };

  /*****************************************************************************
   * Dataproviders
   *****************************************************************************/
  /** List of ItemOptionMaster objects. */
  mThis.optionMasterList = ItemAdminService.getOptions();

  /** List of <b>ItemListItems</b> with options. */
  mThis.sourceOptionItems = ItemAdminService.getItems().filter(function (ili) {
    return (
      [
        SERVER.BooksDBConstants.IT_PRODUCT,
        SERVER.BooksDBConstants.IT_DONATION
      ].indexOf(ili.type) > -1
    );
  });

  /***************************************************************************
   * disableOptionLoad */
  /**
   * Used to disable the load button. Returns true when a valid selection has
   * been made.
   *
   * @return  Boolean  Indication to disable/enable button
   ***************************************************************************/
  mThis.disableOptionLoad = function () {
    return !mThis.sourceOptionItem || typeof mThis.sourceOptionItem != 'object';
  };

  /***************************************************************************
   * handleOption */
  /**
   * Event handling for adding/editing item options.
   *
   * @param  opt    Option to modify. If null, a new option is added.
   ***************************************************************************/
  mThis.handleOption = function (opt) {
    /*------------------------------*/
    /* Dialog already active, exit. */
    /*------------------------------*/
    if (mOptionDialogActive) return;

    var addMode = !opt ? true : false;
    var allowPriceChange = !mThis.dialogCtrl.uiFlags.isDonation;
    var activeOptionMasters = mThis.optionMasterList.filter(function (om) {
      return om.active;
    });

    /*---------------------------------------*/
    /* Call the dialog to add/modify option. */
    /*---------------------------------------*/
    mOptionDialogActive = true;
    AddChangeItemOptionDialog.show(
      addMode,
      opt,
      allowPriceChange,
      activeOptionMasters,
      !mThis.dialogCtrl.hideOptionQtyChange
    )
      .then(function (options) {
        /*------------------------------*/
        /* New option, add to the grid. */
        /*------------------------------*/
        if (addMode) {
          var duplicates = options.filter(function (io) {
            return isDuplicate(io);
          });

          /*--------------------------------------------------*/
          /* Restrict adding a single option when duplicated. */
          /*--------------------------------------------------*/
          if (options.length == 1 && duplicates.length == 1) {
            Alert.info(
              'An item option has already been set up for this product that matches this configuration.',
              'Duplicate Option'
            );
            return;
          }

          /*----------------------------------------------*/
          /* Otherwise add non-duplicated options setting */
          /* the temporary id for inventory referencing   */
          /*----------------------------------------------*/
          options.forEach(function (io) {
            if (duplicates.indexOf(io) == -1) {
              io.id = --mCurrTempID;
              mThis.dgOptions.data.push(io);
            }
          });
          updateSortValues();
        } else {
          /*---------------------------------------------------------------*/
          /* Modifying an existing option, replace it the current in grid. */
          /*---------------------------------------------------------------*/
          var curRowIndex = mThis.dgOptions.data.indexOf(opt);
          mThis.dgOptions.data[curRowIndex] = options[0];
        }
      })
      ['finally'](function () {
        mOptionDialogActive = false;
      });
  };

  /***************************************************************************
   * handleOptionLoad */
  /**
   * Load button click handler to load the existing options from another item.
   ***************************************************************************/
  mThis.handleOptionLoad = function () {
    /*-----------------------------------*/
    /* Ensure an item has been selected. */
    /*-----------------------------------*/
    if (mThis.disableOptionLoad()) return;

    /*---------------------------------------------------*/
    /* Call service to load item options for the chosen  */
    /* product. Wipe the id and set active before adding */
    /*---------------------------------------------------*/
    BusyScreen.setBusy(true);
    BooksSvc.getItemOptionList(mThis.sourceOptionItem.id).then(function (resp) {
      BusyScreen.setBusy(false);
      var listOptions = (resp.data || []).map(function (io) {
        io.id = 0;
        io.active = true;

        /*----------------------------------------------*/
        /* Clear the price change if it is not allowed. */
        /*----------------------------------------------*/
        if (mThis.dialogCtrl.uiFlags.isDonation) io.priceChange = 0;

        return io;
      });

      /*-------------------------------------------------*/
      /* Only add the options that don't currently exist */
      /*-------------------------------------------------*/
      angular.forEach(listOptions, function (io) {
        if (!isDuplicate(io)) {
          io.id = --mCurrTempID;
          mThis.dgOptions.data.push(io);
        }
      });
      updateSortValues();

      /*------------------------------------------*/
      /* Trigger a refresh to update visible rows */
      /*------------------------------------------*/
      mThis.dgOptions.api.core.refresh();

      /*----------------------------*/
      /* Clear the source selection */
      /*----------------------------*/
      mThis.sourceOptionItem = null;
    });
  };

  /***************************************************************************
   * isDuplicate */
  /**
   * Returns an indication if the provided option already exists in the grid.
   *
   * @param   option  ItemOption to check
   * @returns         Boolean indication if a matching option exists
   ***************************************************************************/
  function isDuplicate(option) {
    return (mThis.dgOptions.data || []).some(function (itemOpt) {
      return (
        itemOpt.id != option.id &&
        itemOpt.optionID == option.optionID &&
        itemOpt.value == option.value
      );
    });
  }

  /***************************************************************************
   * isOptionSelected */
  /**
   * Returns true is a selection has been made in the options grid, false
   * otherwise.
   *
   * @return         Boolean indication an item has been selected.
   ***************************************************************************/
  mThis.isOptionSelected = function () {
    return (
      mThis.dgOptions.api &&
      mThis.dgOptions.api.selection.getSelectedRows().length > 0
    );
  };

  /***************************************************************************
   * moveOption */
  /**
   * Click handler for the directional move buttons to change the sort order
   * of the applied ItemOption objects.
   *
   * @param   direction  Direction to move item. True=Move up, false=down.
   ***************************************************************************/
  mThis.moveOption = function (direction) {
    /*---------------------------------*/
    /* Nothing selected to move. Exit. */
    /*---------------------------------*/
    if (!mThis.isOptionSelected()) return;

    /*----------------*/
    /* Get selection. */
    /*----------------*/
    var curOption = mThis.dgOptions.api.selection.getSelectedRows()[0];

    /*------------------------*/
    /* Get selection's index. */
    /*------------------------*/
    var curRowIndex = mThis.dgOptions.data.indexOf(curOption);

    /*-------------------------------------------------------------*/
    /* Get the new position for the option depending on direction. */
    /*-------------------------------------------------------------*/
    var newRowIndex;
    if (direction) newRowIndex = curRowIndex - 1;
    else newRowIndex = curRowIndex + 1;

    /*---------------------------------------------------------*/
    /* The move will place the item out of array bounds, exit. */
    /*---------------------------------------------------------*/
    if (newRowIndex < 0 || newRowIndex > mThis.dgOptions.data.length - 1)
      return;

    /*------------------------------------------------------------*/
    /* Swap selected option with the option at it's new location. */
    /*------------------------------------------------------------*/
    mThis.dgOptions.data[curRowIndex] = mThis.dgOptions.data[newRowIndex];
    mThis.dgOptions.data[newRowIndex] = curOption;
    updateSortValues();

    /*---------------------------------------------------------*/
    /* Scroll to option's new location to keep it in viewport. */
    /*---------------------------------------------------------*/
    var row = mThis.dgOptions.data[newRowIndex];
    var col = mThis.dgOptions.columnDefs[0];
    $timeout(function () {
      mThis.dgOptions.api.core.scrollTo(row, col);
    });
  };

  /***************************************************************************
   * $onInit */
  /**
   * When the parent controller is available, set up local properties.
   ***************************************************************************/
  mThis.$onInit = function () {
    /*----------------------------------*/
    /* Filter out the working item from */
    /* the available items with options */
    /*----------------------------------*/
    mThis.sourceOptionItems = mThis.sourceOptionItems.filter(function (ili) {
      return ili.id != mThis.item.id;
    });

    /*----------------------------------------------*/
    /* Set visibility of the quantity change column */
    /*----------------------------------------------*/
    mColDefQtyChange.visible = !mThis.dialogCtrl.hideOptionQtyChange;

    /*-------------------------------------------------------------------*/
    /* Set up the onSelect tab handler to refresh the grid. A bug in     */
    /* ui-grid causes column headers to disappear when the viewport size */
    /* changes or when a grid hidden in the DOM is eventually shown.     */
    /*-------------------------------------------------------------------*/
    mThis.dialogCtrl.tabOptions.onSelect = function () {
      mThis.dgOptions.api.core.refresh();
    };

    /*------------------------------------------*/
    /* Set initialized to true so grid can load */
    /*------------------------------------------*/
    mThis.initialized = true;
  };

  /***************************************************************************
   * onOptionLoad */
  /**
   * Blur handler for the option load input. When the field blurs, focus is put
   * on the load button if it's not disabled (an option has been selected). This
   * is required as the tab can happen before the digest has updated the value
   * for disabling the "Load" button.
   ***************************************************************************/
  mThis.onOptionLoad = function () {
    if (!mThis.disableOptionLoad())
      $("button[ng-click|='$ioCtrl.handleOptionLoad()']").focus();
  };

  /***************************************************************************
   * optionNameLabelFunction */
  /**
   * Label function to display the appropriate ItemOptionMaster name.
   *
   * @param  option  ItemOption object
   ***************************************************************************/
  function optionNameLabelFunction(option) {
    var match = mThis.optionMasterList
      .filter(function (o) {
        return option.optionID == o.id;
      })
      .shift();
    return match ? match.name : option.optionID || '';
  }

  /***************************************************************************
   * removeOption */
  /**
   * Removes the selected option from the grid.
   *
   * @param  optionToRemove  Option to remove from the grid.
   ***************************************************************************/
  function removeOption(optionToRemove) {
    /*---------------------------*/
    /* Nothing to remove, abort. */
    /*---------------------------*/
    if (!optionToRemove) return;

    /*-----------------------------------------------*/
    /* Product is using the option requested, abort. */
    /*-----------------------------------------------*/
    if (
      (mThis.item.inventoryProducts || []).some(function (ip) {
        return (ip.options || []).some(function (option) {
          return option.itemOptionID == optionToRemove.id;
        });
      })
    ) {
      Alert.info(
        'This option has been tracked in inventory, and cannot be removed.',
        'Cannot Remove Option'
      );
      return;
    }

    /*-------------------------------*/
    /* Prompt user to remove option. */
    /*-------------------------------*/
    Alert.confirm(
      'Are you sure you want to remove this option?',
      'Confirm Remove'
    ).then(function () {
      /*-------------------------------------*/
      /* Remove option. if response from     */
      /* server is error, display to screen. */
      /*-------------------------------------*/
      BusyScreen.setBusy(true);
      ItemAdminService.dropItemOption(optionToRemove.id)
        .then(
          function () {
            Alert.success('Option sucessfully removed.');

            /*------------------------------*/
            /* Remove option from the grid. */
            /*------------------------------*/
            var index = mThis.dgOptions.data.indexOf(optionToRemove);
            mThis.dgOptions.data.splice(index, 1);
            updateSortValues();
          },
          function (resp) {
            Alert.error(
              resp.parsedResponse.message() || 'Error deleting this option',
              'Failed to remove option.'
            );
          }
        )
        ['finally'](function () {
          BusyScreen.setBusy(false);
        });
    });
  }

  /***************************************************************************
   * updateSortValues */
  /**
   * Updates the sortValue property on each ItemOption from its position in the
   * array as this will be used in the the POS apps for displaying the values.
   ***************************************************************************/
  function updateSortValues() {
    for (var i = 0; i < mThis.item.itemOptions.length; i++)
      mThis.item.itemOptions[i].sortValue = i;
  }

  /*---------------------------------------------------*/
  /* Set up an accessor the item property from the     */
  /* dialogCtrl. Use an empty object for data binding. */
  /*---------------------------------------------------*/
  Object.defineProperty(mThis, 'item', {
    get: function () {
      return mThis.dialogCtrl ? mThis.dialogCtrl.item : {};
    }
  });

  /*--------------------------------------------*/
  /* Set up listener for the row delete event   */
  /*--------------------------------------------*/
  $scope.$on(
    CommonUIGridTemplates.EVENT_DELETE_ROW,
    function (event, grid, row) {
      removeOption(row.entity);
    }
  );

  /*----------------------------------------------*/
  /* Set up a watcher for the item reference.     */
  /* When changed, set the dgOptions data source. */
  /*----------------------------------------------*/
  $scope.$watch(
    function () {
      return mThis.item;
    },
    function (item) {
      if (!item) return;

      /*----------------------*/
      /* Ensure a data source */
      /*----------------------*/
      if (!item.itemOptions) item.itemOptions = [];

      mThis.dgOptions.data = item.itemOptions;
    }
  );
}
